import React, { useState } from "react";
import { dweenColorway } from "utils/constants";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import { T } from "services/lang/LanguageService";
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";
import {
  ProductTemplateEntryType,
  useGetProductTemplateData,
} from "api/api-hooks/product-templates-hooks";
import { ProgressSpinner } from "primereact/progressspinner";
import DatasetBarChart from "../charts/bar-chart/DatasetBarChart";
import { defaultFromDateTime, defaultToDateTime } from "../charts/utils";
import { PlotDataEx } from "../charts/interfaces";
import {
  AggregationOption,
  aggregationOptions,
  IProductTemplateBarChart,
} from "./product-template-bar-chart-editor/ProductTemplateBarChartTypes";

export default function ProductTemplateBarChart(props: IProductTemplateBarChart) {
  const [from, setFrom] = useState<Date>(() => {
    return props.from
      ? new Date(props.from)
      : defaultFromDateTime({
          loadConf: {
            ...props.loadConf,
            alignUnit: props.loadConf?.alignUnit ?? "MINUTES",
            lag: props.loadConf?.lag ?? 6,
            lagUnit: props.loadConf?.lagUnit ?? "HOURS",
            lead: props.loadConf?.lead ?? 0,
            leadUnit: props.loadConf?.leadUnit ?? "HOURS",
          },
          referenceTime: props.referenceTime,
        });
  });

  const [to, setTo] = useState<Date>(() => {
    return props.to
      ? new Date(props.to)
      : defaultToDateTime({
          loadConf: {
            ...props.loadConf,
            alignUnit: props.loadConf?.alignUnit ?? "MINUTES",
            lag: props.loadConf?.lag ?? 6,
            lagUnit: props.loadConf?.lagUnit ?? "HOURS",
            lead: props.loadConf?.lead ?? 0,
            leadUnit: props.loadConf?.leadUnit ?? "HOURS",
          },
          referenceTime: props.referenceTime,
        });
  });

  const [rawFrom, setRawFrom] = useState(from);
  const [rawTo, setRawTo] = useState(to);

  const applyDateRange = () => {
    setFrom(rawFrom);
    setTo(rawTo);
  };

  const params: ProductTemplateEntryType = {
    safeName: props.safeName,
    dateFrom: from.toISOString(),
    dateTo: to.toISOString(),
  };

  const { data: templateData, isLoading } = useGetProductTemplateData(params, {
    enabled: !!props.safeName && !!from && !!to,
  });

  if (isLoading)
    return (
      <div className="demo-container p-p-4">
        <ProgressSpinner className="p-d-block p-mx-auto" />
      </div>
    );

  const nameToAggregate = new Map<
    string,
    { total: number; count: number; min: number; max: number }
  >();

  templateData?.forEach(entry => {
    const timeStr = entry.entries?.[props.timeColumn]?.value;
    const name = entry.entries?.[props.nameColumn]?.value?.toString();
    const value = Number(entry.entries?.[props.valueColumn]?.value);

    if (!timeStr || !name || value == null || Number.isNaN(value) || typeof timeStr !== "string")
      return;

    const entryTime = new Date(timeStr);
    if (entryTime >= from && entryTime <= to) {
      const existing = nameToAggregate.get(name) ?? {
        total: 0,
        count: 0,
        min: value,
        max: value,
      };

      existing.total += value;
      existing.count += 1;
      existing.min = Math.min(existing.min, value);
      existing.max = Math.max(existing.max, value);

      nameToAggregate.set(name, existing);
    }
  });

  const plotData = Array.from(nameToAggregate.entries()).map(([name, agg], index) => {
    let yValue: number;

    switch (props.aggregationType) {
      case AggregationOption.AVG:
        yValue = agg.total / agg.count;
        break;
      case AggregationOption.MIN:
        yValue = agg.min;
        break;
      case AggregationOption.MAX:
        yValue = agg.max;
        break;
      case AggregationOption.COUNT:
        yValue = agg.count;
        break;
      case AggregationOption.SUM:
      default:
        yValue = agg.total;
        break;
    }

    return {
      hovertemplate: `%{y} ${props.valueUnit}, %{x}<br><extra></extra>`,
      marker: {
        color: dweenColorway[index % dweenColorway.length],
      },
      text: [`${yValue} ${props.valueUnit}`],
      type: "bar",
      unit: props.valueUnit,
      x: [name],
      y: [yValue],
      name,
    };
  });

  return (
    <>
      <div className="flex flex-row justify-content-between">
        <div className="flex flex-row gap-2">
          <CalendarWrapper value={rawFrom} onChange={setRawFrom} />
          <CalendarWrapper value={rawTo} onChange={setRawTo} />
          <Button
            icon="pi pi-sync"
            className="component-editor-button h-full"
            tooltip={T("action.apply-date-range")}
            tooltipOptions={{ position: "bottom" }}
            onClick={applyDateRange}
          />
        </div>
        <div className="flex align-items-center gap-2">
          <span className="font-semibold text-color-secondary">{T("label.aggregation-type")}:</span>
          <Tag
            value={
              aggregationOptions.find(opt => opt.value === props.aggregationType)?.label ?? "Sum"
            }
            className="px-3 py-1 text-sm component-editor-button"
          />
        </div>
      </div>
      <DatasetBarChart
        data={plotData as PlotDataEx[]}
        height={props.height ?? 400}
        width={props.width}
        xaxisType="category"
        skipWarning
      />
    </>
  );
}
